// src/utils/format.js - Czech formatting helpers (dates, money, plurals).
import { addDays, differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { cs } from 'date-fns/locale';

function toDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : parseISO(String(value));
  return isValid(d) ? d : null;
}

/** "5. 3. 2027" */
export function formatDate(value) {
  const d = toDate(value);
  return d ? format(d, 'd. M. yyyy', { locale: cs }) : '';
}

/** "5. března 2027" */
export function formatDateLong(value) {
  const d = toDate(value);
  return d ? format(d, 'd. MMMM yyyy', { locale: cs }) : '';
}

/** "5. 3. 2027 14:05" */
export function formatDateTime(value) {
  const d = toDate(value);
  return d ? format(d, 'd. M. yyyy H:mm', { locale: cs }) : '';
}

/** Whole days from today to the date (negative = in the past). */
export function daysUntil(value) {
  const d = toDate(value);
  return d ? differenceInCalendarDays(d, new Date()) : null;
}

/** Today as "YYYY-MM-DD" in the browser's (the organiser's) time zone - not UTC. */
export function todayIso(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Has the competition already taken place? By its date, not its status: statuses are set by
 * hand and old rounds are often left "upcoming"/"active"/"closed". The server's `isPast`
 * (Prague day) wins when present; the competition day itself is not past.
 */
export function isPastCompetition(competition) {
  if (!competition) return false;
  if (typeof competition.isPast === 'boolean') return competition.isPast;
  const days = daysUntil(competition.date);
  return days !== null && days < 0;
}

/** The ISO day `days` days after `value` (negative = before), "YYYY-MM-DD"; null without a date. */
export function shiftDate(value, days) {
  const d = toDate(value);
  return d ? todayIso(addDays(d, days)) : null;
}

/**
 * Last day of the online presence (and of paying the entry fee): a week before the
 * competition. The server's `presenceDeadline` wins when present.
 */
export function presenceDeadline(competition) {
  if (!competition) return null;
  if (competition.presenceDeadline) return competition.presenceDeadline;
  return shiftDate(competition.date, -7);
}

/** Last day to upload the Grand Finále background video: two weeks before the finale. */
export function backgroundVideoDeadline(competition) {
  return competition ? shiftDate(competition.date, -14) : null;
}

/**
 * Is the registration closed (deadline passed, or closed / finished by the organiser)?
 * The server's `registrationClosed` wins; otherwise the status and `registrationEnd` decide.
 */
/** The registration deadline day has passed (the round can no longer be entered). */
export function registrationDeadlinePassed(competition) {
  const days = daysUntil(competition && competition.registrationEnd);
  return days !== null && days < 0;
}

/**
 * Does a round belong in the group's "Soutěže" / "Nejbližší soutěže"? Before its registration
 * deadline always; after it only for a group that has a choreography there (its own event, until
 * the day) - the others could not enter it any more, so for them it disappears as it always did.
 */
export function showInCompetitionLists(competition, entries) {
  return !registrationDeadlinePassed(competition) || entries > 0;
}

export function isRegistrationClosed(competition) {
  if (!competition) return false;
  if (typeof competition.registrationClosed === 'boolean') return competition.registrationClosed;
  if (['closed', 'finished', 'archived'].includes(competition.status)) return true;
  const days = daysUntil(competition.registrationEnd);
  return days !== null && days < 0;
}

/** "1 250 Kč" / "30,30 €" - never labels EUR as Kč (INV-16, INV-27). */
export function formatMoney(amount, currency = 'CZK') {
  const value = Number(amount || 0);
  const code = currency === 'EUR' ? 'EUR' : 'CZK';
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: code === 'EUR' || !Number.isInteger(value) ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Czech plural: plural(n, ['tanečník', 'tanečníci', 'tanečníků']) */
export function plural(n, [one, few, many]) {
  if (n === 1) return one;
  if (n >= 2 && n <= 4) return few;
  return many;
}

export function countLabel(n, forms) {
  return `${n} ${plural(n, forms)}`;
}
